import { useEffect, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import { refreshAuthToken } from '../utils/tokenRefresh'

const REFRESH_CHECK_INTERVAL_MS = 30 * 1000 // Check every 30 seconds
const REFRESH_BUFFER_MS = 5 * 60 * 1000 // Start refresh 5 minutes before expiry

export const useTokenRefresh = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const logout = useAuthStore((state) => state.logout)
  const user = useAuthStore((state) => state.user)
  const isRefreshingRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !user?.expireAt) return

    const checkAndRefreshToken = async () => {
      // Prevent concurrent refresh attempts
      if (isRefreshingRef.current) {
        console.log('Token refresh already in progress, skipping...')
        return
      }

      if (!user.expireAt) return

      const expiryDate = new Date(user.expireAt)
      const now = new Date()
      const timeUntilExpiry = expiryDate.getTime() - now.getTime()

      // If token is already expired, logout immediately
      if (timeUntilExpiry < 0) {
        console.log('Token already expired, logging out...', {
          expiresAt: expiryDate.toISOString(),
          expiredAgo:
            Math.abs(Math.floor(timeUntilExpiry / 1000 / 60)) + ' minutes',
        })
        await logout()
        return
      }

      // If token expires soon (within buffer), refresh it
      if (timeUntilExpiry < REFRESH_BUFFER_MS) {
        try {
          isRefreshingRef.current = true
          console.log('Token expiring soon, refreshing...', {
            expiresAt: expiryDate.toISOString(),
            timeUntilExpiry:
              Math.floor(timeUntilExpiry / 1000 / 60) + ' minutes',
          })
          await refreshAuthToken()
        } catch (error) {
          console.error('Failed to refresh token:', error)
          await logout()
        } finally {
          isRefreshingRef.current = false
        }
      }
    }

    // Check immediately on mount
    checkAndRefreshToken()

    // Set up interval to check periodically
    const intervalId: NodeJS.Timeout = setInterval(
      checkAndRefreshToken,
      REFRESH_CHECK_INTERVAL_MS,
    )

    return () => {
      clearInterval(intervalId)
    }
  }, [isAuthenticated, logout, user?.expireAt])
}
